import type { VinculoFoto } from '@adelante/shared';
import { cliente } from './cliente';

/**
 * Fotos: compresión en el teléfono + subida directa a Azure Blob.
 *
 *   1. Se reduce a máx. 1600 px (JPEG ~0.8) y se genera una miniatura de 320 px.
 *   2. La API entrega URLs SAS de subida (15 min).
 *   3. PUT directo al Blob (no pasa por la Function: sin límites de tamaño).
 *   4. Se confirma a la API, que verifica el blob y lo vincula.
 *
 * Las fotos de fichas se suben directo (subirFoto). Las fotos de obra pasan
 * por la cola offline (colaFotos.ts), que usa comprimir* + subirComprimida.
 */

const LADO_MAX = 1600;
const LADO_THUMB = 320;

type Fuente = CanvasImageSource & { width: number; height: number };

export interface FotoComprimida {
  principal: Blob;
  mini: Blob;
  ancho: number;
  alto: number;
}

export interface Ubicacion {
  latitud: number;
  longitud: number;
  precision_m: number;
}

async function cargarImagen(archivo: Blob): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap respeta la orientación EXIF en navegadores modernos.
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(archivo, { imageOrientation: 'from-image' });
    } catch {
      /* fallback abajo */
    }
  }
  const url = URL.createObjectURL(archivo);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function redimensionar(
  img: Fuente,
  ladoMax: number,
  calidad: number,
): Promise<{ blob: Blob; ancho: number; alto: number }> {
  const escala = Math.min(1, ladoMax / Math.max(img.width, img.height));
  const ancho = Math.round(img.width * escala);
  const alto = Math.round(img.height * escala);
  const canvas = document.createElement('canvas');
  canvas.width = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no permite procesar imágenes');
  ctx.drawImage(img, 0, 0, ancho, alto);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve({ blob, ancho, alto }) : reject(new Error('No se pudo comprimir'))),
      'image/jpeg',
      calidad,
    ),
  );
}

/** Comprime una imagen ya dibujable (p. ej. el cuadro capturado de la cámara). */
export async function comprimirFuente(fuente: Fuente): Promise<FotoComprimida> {
  const principal = await redimensionar(fuente, LADO_MAX, 0.8);
  const mini = await redimensionar(fuente, LADO_THUMB, 0.7);
  return {
    principal: principal.blob,
    mini: mini.blob,
    ancho: principal.ancho,
    alto: principal.alto,
  };
}

/** Comprime un archivo de imagen (cámara nativa o galería). */
export async function comprimirArchivo(archivo: Blob): Promise<FotoComprimida> {
  const img = await cargarImagen(archivo);
  try {
    return await comprimirFuente(img);
  } finally {
    if ('close' in img) img.close();
  }
}

async function putBlob(url: string, blob: Blob): Promise<void> {
  const r = await fetch(url, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/jpeg' },
    body: blob,
  });
  if (!r.ok) throw new Error(`No se pudo subir la foto (HTTP ${r.status})`);
}

/** Sube una foto ya comprimida y la vincula. Devuelve el id. */
export async function subirComprimida(
  foto: FotoComprimida,
  vinculo: VinculoFoto & { nota?: string | null },
  extra: { tomada_en: string; ubicacion?: Ubicacion | null },
): Promise<string> {
  const sub = await cliente.calidad.pedirSubidaFoto();
  if (sub.error !== null) throw new Error(sub.error.mensaje);
  const { id, upload_url, thumb_upload_url } = sub.data;

  await putBlob(upload_url, foto.principal);
  await putBlob(thumb_upload_url, foto.mini).catch(() => {
    /* la miniatura es opcional */
  });

  const conf = await cliente.calidad.confirmarFoto({
    id,
    ...vinculo,
    ancho: foto.ancho,
    alto: foto.alto,
    tomada_en: extra.tomada_en,
    latitud: extra.ubicacion?.latitud ?? null,
    longitud: extra.ubicacion?.longitud ?? null,
    precision_m: extra.ubicacion?.precision_m ?? null,
  });
  if (conf.error !== null) throw new Error(conf.error.mensaje);
  return id;
}

/** Comprime, sube y vincula una foto de inmediato (fichas de control). */
export async function subirFoto(
  archivo: File,
  vinculo: VinculoFoto & { nota?: string | null },
): Promise<string> {
  const foto = await comprimirArchivo(archivo);
  return subirComprimida(foto, vinculo, {
    tomada_en: new Date(archivo.lastModified || Date.now()).toISOString(),
  });
}
